import type { VoiceNavPendingOpenModalityWorkflow } from '../../api/apiConfig'
import type { ChatMessage } from '../../types/chat'
import { chatHasPendingDeferredModality } from '../../utils/crossPatientEcgFlow'
import { looksLikePatientChartSlotLabel, patientNamesLikelyEqual } from '../../utils/patientSwitchConfirm'
import { hostOrSessionChartMatchesModalitySwitchTarget } from '../../utils/modalitySwitchHostSyncRecovery'
import type { ActivePatientSyncPayload, DeferredReplaySessionContext } from './types'
import {
  latestOpenPatientPayloadFromChatMessages,
  latestPendingModalityPatientPayloadFromChatMessages,
  mergeActivePatientPayloadForSessionSync,
} from './activePatientFromChatCore'

export type MutableRefLike<T> = { current: T }

/**
 * ``mergeActivePatientPayloadForSessionSync`` prefers the *second* argument; when the inner merge
 * is session+workflow and the workflow still carries a slot label (``patient 2``) while chat
 * already has the registry name from ``open_patient``, prefer chat so POST /active-patient does
 * not overwrite the display name with the typed token.
 */
function mergeChatWithSessionWorkflowPreferRegistry(
  chat: ActivePatientSyncPayload | null,
  inner: ActivePatientSyncPayload | null,
): ActivePatientSyncPayload | null {
  const innerName = (inner?.patient_name || '').trim()
  if (looksLikePatientChartSlotLabel(innerName) && (chat?.patient_name || '').trim()) {
    return mergeActivePatientPayloadForSessionSync(inner, chat)
  }
  return mergeActivePatientPayloadForSessionSync(chat, inner)
}

export function shouldRunDeferredEcgReplayPipeline(input: {
  restoredMessages: readonly ChatMessage[]
  sessionContext: DeferredReplaySessionContext | null
  pendingUiReplayDone: boolean
  consumeBrowserDeferredModalityHint: () => boolean
  /** Host chart from embed; used when ``pending_open_modality_workflow`` awaits switch but Redis session is stale. */
  hostPatient?: ActivePatientSyncPayload | null
}): boolean {
  const pr = input.sessionContext?.pending_ui_replay
  const dog = input.sessionContext?.deferred_open_ecg
  const hasDeferredOpenEcg =
    Boolean(dog?.pending) && !dog?.expired && !input.pendingUiReplayDone
  const hasDeferredChat = chatHasPendingDeferredModality(input.restoredMessages)
  const redisDeferred =
    Boolean(pr?.deferred) && !pr?.expired && !input.pendingUiReplayDone
  const redisArmed = Boolean(pr?.armed) && !pr?.expired && !input.pendingUiReplayDone
  const pendingCmd = (pr?.pending_command ?? '').trim()
  const redisPendingCommandQueued =
    pendingCmd.length > 0 && !pr?.expired && !input.pendingUiReplayDone
  const wf = input.sessionContext?.pending_open_modality_workflow
  const pendingOpenModalityWorkflowQueued =
    (wf?.kind === 'open_modality_for_patient' ||
      wf?.kind === 'view_prescription_for_patient' ||
      wf?.kind === 'sports_noc_for_patient' ||
      wf?.kind === 'renew_ordonnance_for_patient') &&
    wf.awaiting_switch_confirm === true &&
    !input.pendingUiReplayDone
  const pendingOpenModalityAwaitingChartMatch =
    pendingOpenModalityWorkflowQueued &&
    hostOrSessionChartMatchesModalitySwitchTarget({
      workflow: wf as VoiceNavPendingOpenModalityWorkflow,
      hostPatient: input.hostPatient ?? null,
      sessionActivePatient: input.sessionContext?.active_patient ?? null,
    })
  return (
    hasDeferredOpenEcg ||
    hasDeferredChat ||
    redisDeferred ||
    redisArmed ||
    redisPendingCommandQueued ||
    pendingOpenModalityWorkflowQueued ||
    pendingOpenModalityAwaitingChartMatch ||
    input.consumeBrowserDeferredModalityHint()
  )
}

export function shouldRetryDeferredReplayAfterFirstAttempt(input: {
  sessionContextAfter: DeferredReplaySessionContext | null
  pendingUiReplayDone: boolean
}): boolean {
  const wfAfter = input.sessionContextAfter?.pending_open_modality_workflow
  if (
    (wfAfter?.kind === 'open_modality_for_patient' ||
      wfAfter?.kind === 'view_prescription_for_patient' ||
      wfAfter?.kind === 'sports_noc_for_patient' ||
      wfAfter?.kind === 'renew_ordonnance_for_patient') &&
    wfAfter.awaiting_switch_confirm &&
    !input.pendingUiReplayDone
  ) {
    const dog0 = input.sessionContextAfter?.deferred_open_ecg
    if (!dog0?.pending) {
      return true
    }
  }
  const pr = input.sessionContextAfter?.pending_ui_replay
  const dog = input.sessionContextAfter?.deferred_open_ecg
  if (
    dog &&
    Boolean(dog.pending) &&
    !dog.expired &&
    !dog.replay_ready &&
    !input.pendingUiReplayDone
  ) {
    return true
  }
  return Boolean(
    pr?.deferred &&
      !pr?.expired &&
      !pr?.replay_ready &&
      !input.pendingUiReplayDone,
  )
}

function workflowTargetPatientPayload(
  ctx: DeferredReplaySessionContext | null,
): ActivePatientSyncPayload | null {
  const wf = ctx?.pending_open_modality_workflow
  if (
    (wf?.kind !== 'open_modality_for_patient' &&
      wf?.kind !== 'view_prescription_for_patient' &&
      wf?.kind !== 'sports_noc_for_patient' &&
      wf?.kind !== 'renew_ordonnance_for_patient') ||
    !wf.awaiting_switch_confirm
  ) {
    return null
  }
  const patient_name = (wf.target_patient_name || '').trim()
  if (!patient_name) return null
  return { patient_name }
}

/** Merge host chart + transcript + server session for ``POST /session/active-patient``. */
export function buildMergedActivePatientForDeferredReplay(input: {
  hostPatient: ActivePatientSyncPayload | null
  restoredMessages: readonly ChatMessage[]
  loadedSessionContext: DeferredReplaySessionContext | null
}): ActivePatientSyncPayload | null {
  const workflowPayload = workflowTargetPatientPayload(input.loadedSessionContext)
  let chatPayload =
    latestPendingModalityPatientPayloadFromChatMessages(input.restoredMessages) ??
    latestOpenPatientPayloadFromChatMessages(input.restoredMessages)
  // Command alias in workflow (e.g. "nejam") must not keep stale chart ids from a prior open_patient row.
  if (workflowPayload?.patient_name && chatPayload) {
    const wfName = (workflowPayload.patient_name || '').trim()
    const chatName = (chatPayload.patient_name || '').trim()
    if (
      wfName &&
      chatName &&
      !patientNamesLikelyEqual(wfName, chatName) &&
      !looksLikePatientChartSlotLabel(wfName) &&
      ((chatPayload.patient_id || '').trim() || (chatPayload.patient_unique_id || '').trim())
    ) {
      chatPayload = { patient_name: wfName }
    }
  }
  let sessionPayload =
    input.loadedSessionContext?.active_patient &&
    ((input.loadedSessionContext.active_patient.patient_id || '').trim() ||
      (input.loadedSessionContext.active_patient.patient_name || '').trim())
      ? input.loadedSessionContext.active_patient
      : null
  // After switch Yes the host may show Nejam while Redis session still has patient 1 — do not sync stale chart.
  if (workflowPayload && sessionPayload?.patient_name) {
    const sn = (sessionPayload.patient_name || '').trim()
    const tn = (workflowPayload.patient_name || '').trim()
    if (sn && tn && !patientNamesLikelyEqual(sn, tn)) {
      sessionPayload = null
    }
  }
  const sessionWorkflowMerged = mergeActivePatientPayloadForSessionSync(sessionPayload, workflowPayload)
  const chatMerged = mergeChatWithSessionWorkflowPreferRegistry(chatPayload, sessionWorkflowMerged)
  return mergeActivePatientPayloadForSessionSync(input.hostPatient, chatMerged)
}
